import { SEEKA_TRACKING_TYPE, TrackingBeacon, TrackingBeacons } from '../useTrackingBeacons/transform';
import { generateEventId, sendEventToCourier, useAttributionID } from '@eventbrite/legacy-infra.clid-courier';

type PixelsTrackingOptions = {
  eventId: string;
  eventName: string;
  organizationId: string;
  organizationName: string;
  environment: string;
  debugging: boolean;
  trackingBeacons: TrackingBeacons;
};

export const pixelsTracking = ({
  eventId,
  eventName,
  organizationId,
  organizationName,
  environment,
  debugging,
  trackingBeacons,
}: PixelsTrackingOptions) => {
  import('@eventbrite/legacy-infra.grylls')
    .then((grylls) => {
      const options = {
        environment,
        debugging,
        // These are hardcoded in Core
        enabled: true,
        launchSupportMultipleFacebookPixels: true,
      };

      // Check existence of window before accessing document.title
      const title = window && window.document ? window.document.title : '';

      grylls.load(trackingBeacons, options);

      // Generate synthetic event ID for Facebook deduplication
      const stableId = useAttributionID();
      const syntheticFbEventId = stableId ? generateEventId(stableId) : null;

      // Pass syntheticFbEventId to page() for PageView event deduplication
      grylls.page(title, {
        syntheticFbEventId: syntheticFbEventId,
      });

      // eventName + organizationName are consumed by the Seeka adapter to build
      // a Converge.track.viewContentItem payload; other pixels ignore them.
      grylls.track('event_listing', {
        eventId,
        eventName,
        organizationName,
        syntheticFbEventId,
      });

      if (syntheticFbEventId && stableId && organizationId) {
        // Seeka beacons use their own credential model (orgId/publicKey/instanceId) and aren't
        // routed through the FB CAPI courier — exclude them so we only forward pixel beacons.
        const courierBeacons: Record<string, TrackingBeacon[]> = {};
        for (const [type, beacons] of Object.entries(trackingBeacons)) {
          if (type === SEEKA_TRACKING_TYPE) continue;
          const pixelBeacons = beacons.filter((b): b is TrackingBeacon => 'pixelId' in b);
          if (pixelBeacons.length > 0) courierBeacons[type] = pixelBeacons;
        }

        sendEventToCourier('event_listing', syntheticFbEventId, stableId, organizationId, courierBeacons, true, {
          eventId: eventId,
        });
      }
    })
    .catch((err) => {
      console.error('[pixelsTracking] Failed to initialize tracking pixels:', err);
    });
};
